import type { MaybeRefOrGetter } from '@vueuse/core';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { ValueOf } from '@/helpers/types';
import type { ComputedRef, Ref } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';
import { toValue } from '@vueuse/core';
import { computed, onMounted, onServerPrefetch } from 'vue';

import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';
import { storeToRefs } from 'pinia';

// TYPES
type digits = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type day = `${'0'}${Exclude<digits, '0'>}` | `${'1' | '2'}${digits}` | '30' | '31';
type month = `${'0'}${Exclude<digits, '0'>}` | '10' | '11' | '12';
type year = `20${digits}${digits}`;
type ISO8601 = `${year}-${month}-${day}`;

/** Provider promotions are only for titles with Flatrate and free offers of the promo provider. */
const flatrateOrFreeOrAds = [MonetizationType.Flatrate, MonetizationType.Free, MonetizationType.Ads];

interface PromoText {
	/**
	 * Angled ribbon over square provider icons or
	 * drawer below wide provider icons.
	 */
	ribbon?: string;

	/**
	 * Ribbon text exclusively for wide provider icons
	 * which have room for a bit more text.
	 */
	wideRibbon?: string;

	/**
	 * Text below the square provider in GridOffer (next to 4k/quality).
	 * Text above the price in ListOffer.
	 */
	offerText?: string;

	/** Text that appears on the backdrop play button. */
	longText?: string;

	/** Optional promotion start date in `YYYY-MM-DD` format. */
	startDate?: ISO8601;

	/** `YYYY-MM-DD` format. */
	endDate: ISO8601;

	/** 3 character shortname of the promoted provider. */
	provider: string;

	/** The Url of the promoted provider which overrides the regular offer link. */
	url?: string;

	/** Disclaimer text - for additional Terms and Conditions, passed into the DisclaimerButton */
	disclaimerText?: string;

	/** Secondary disclaimer text for BuyBoxOfferNew - for additional Terms and Conditions, passed into the DisclaimerButton */
	offerDisclaimerText?: string;
}

/** Normal PromoText config with the Provider Icon URL */
export type PromoTextWithIcon = PromoText & { icon: string };

// Change uk to gb in the WebLocale list and return it upper case to match store country
type PromotionLocale = Uppercase<Exclude<keyof typeof WebLocale, 'uk'> | 'gb'>;

const promotions = {
	AR: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Plan Premium de Disney+ a $9,999/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	BO: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $3.99/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	BR: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tempo limitado.',
			longText: 'Partir de R$9,90/mês',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Aplicam-se termos e condições. Mais info em https://www.disneyplus.com/',
			disclaimerText: 'Aplicam-se termos e condições. Mais info em https://www.disneyplus.com/',
		},
	],
	CL: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $2,990/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	CO: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $8,990/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	CR: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $3.99/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	EC: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $3.99/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	GT: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $3.99/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	HN: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $3.99/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	MX: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $49,90/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	NI: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $3.99/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	PA: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $3.99/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	PE: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde S/17,45 por mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	PY: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $3.99/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	SV: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $3.99/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
	VE: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado.',
			longText: 'Desde $3.99/mes por 2 mes.',
			startDate: '2026-03-04',
			endDate: '2026-03-31',
			provider: 'dnp',
			offerDisclaimerText: 'Más info en https://www.disneyplus.com/',
			disclaimerText: 'Más info en https://www.disneyplus.com/',
		},
	],
} satisfies Partial<Record<PromotionLocale, PromoText[]>>;

type PromotionCountries = keyof typeof promotions;
type PromotionProviders = ValueOf<typeof promotions>[number]['provider'];

const promotionProviderIcons: Record<PromotionProviders, string> = {
	amp: `${JW_CONFIG.IMAGESCALER_URL}/icon/52449539/s100/amazonprime.webp`,
	prv: `${JW_CONFIG.IMAGESCALER_URL}/icon/52449539/s100/amazonprime.webp`,
	hst: `${JW_CONFIG.IMAGESCALER_URL}/icon/324950024/s100/hotstar.webp`,
	dnp: `${JW_CONFIG.IMAGESCALER_URL}/icon/313118777/s100/disneyplus.webp`,
	atp: `${JW_CONFIG.IMAGESCALER_URL}/icon/152862153/s100/appletvplus.webp`,
	bng: `${JW_CONFIG.IMAGESCALER_URL}/icon/183470172/s100/binge.webp`,
	pmp: `${JW_CONFIG.IMAGESCALER_URL}/icon/242706661/s100/paramountplus.webp`,
	sst: `${JW_CONFIG.IMAGESCALER_URL}/icon/300772362/s100/skyshowtime.webp`,
	mxx: `${JW_CONFIG.IMAGESCALER_URL}/icon/305458112/s100/max.webp`,
	tbv: `${JW_CONFIG.IMAGESCALER_URL}/icon/313528601/s100/tubitv.webp`,
	cts: `${JW_CONFIG.IMAGESCALER_URL}/icon/48600036/s100/curiositystream.webp`,
};

/** SSR Safe way to get active provider promotions. */
export function useProviderPromotion(title?: MaybeRefOrGetter<TitleDetail>): {
	titleHasProviderPromotion: ComputedRef<boolean>;
	isPromotionOffer: (offer: TitleOfferFragment) => boolean;
	providerPromotion: Ref<PromoTextWithIcon | null>;
} {
	const titleDetailsStore = useTitleDetailsStore();
	const { setProviderPromotion } = titleDetailsStore;
	const { providerPromotion } = storeToRefs(titleDetailsStore);

	onServerPrefetch(() => setProviderPromotion(toValue(title)));

	// refetch on client (no hydration error) to get the updated status
	onMounted(() => setProviderPromotion(toValue(title)));

	const titleHasProviderPromotion = computed(() => providerPromotion.value != null);

	function isPromotionOffer(offer: TitleOfferFragment) {
		return (
			flatrateOrFreeOrAds.includes(offer.monetizationType) &&
			(providerPromotion.value as PromoTextWithIcon | null)?.provider === offer.package.shortName
		);
	}

	return {
		/** Is a provider promotion applicable to the current title. */
		titleHasProviderPromotion,

		/** Check if the offer is under the current title's provider promotion. */
		isPromotionOffer,

		/** The applicable promotion itself. */
		providerPromotion,
	};
}

export function getPromoText(
	country: string,
	title: TitleDetail | null = null,
	currentDate: Date = new Date()
): PromoTextWithIcon | null {
	const promos = promotions[country as PromotionCountries];

	if (!promos || promos.length === 0 || title == null || title.offers.length === 0) return null;

	const promoText = promos.find(promo =>
		title.offers.some(
			offer => flatrateOrFreeOrAds.includes(offer.monetizationType) && offer.package.shortName === promo.provider
		)
	);

	if (promoText == null) return null;
	// Checking if we're within the promo dates
	if (promoText.startDate && +currentDate < new Date(promoText.startDate).setUTCHours(0, 0, 0, 0)) return null;
	if (promoText.endDate && +currentDate > new Date(promoText.endDate).setUTCHours(23, 59, 59, 999)) return null;

	return { ...promoText, icon: promotionProviderIcons[promoText.provider] };
}

// Disney External Pixel Tracking
const oceaniaLocales = new Set([WebLocale.au, WebLocale.nz]);
const noramLocales = new Set([WebLocale.us, WebLocale.ca]);
const emeaLocales = new Set([
	WebLocale.at,
	WebLocale.be,
	WebLocale.cz,
	WebLocale.dk,
	WebLocale.fi,
	WebLocale.fr,
	WebLocale.de,
	WebLocale.gr,
	WebLocale.hu,
	WebLocale.ie,
	WebLocale.it,
	WebLocale.lu,
	WebLocale.nl,
	WebLocale.no,
	WebLocale.pl,
	WebLocale.pt,
	WebLocale.ro,
	WebLocale.sk,
	WebLocale.es,
	WebLocale.se,
	WebLocale.ch,
	WebLocale.uk,
	WebLocale.tr,
]);
const latamLocales = new Set([
	WebLocale.br,
	WebLocale.mx,
	WebLocale.ar,
	WebLocale.cl,
	WebLocale.co,
	WebLocale.pe,
	WebLocale.uy,
	WebLocale.ec,
	WebLocale.pa,
	WebLocale.cr,
	WebLocale.py,
	WebLocale.bo,
	WebLocale.gt,
	WebLocale.ni,
	WebLocale.do,
	WebLocale.sv,
	WebLocale.hn,
	WebLocale.ve,
]);

export function getDisneyImpressionUrl(providerShortName: string | null | undefined, webLocale: WebLocale) {
	if (providerShortName === 'dnp' /* Disney+ */) {
		if (oceaniaLocales.has(webLocale) || noramLocales.has(webLocale))
			return 'https://imp.pxf.io/i/1206980/1833790/9358?sharedid=web';

		if (latamLocales.has(webLocale) || emeaLocales.has(webLocale))
			return 'https://imp.pxf.io/i/1206980/1848147/9358?sharedid=web';
	}

	if (providerShortName === 'srp' /* Star+ */) {
		if (latamLocales.has(webLocale)) return 'https://imp.pxf.io/i/1206980/1584316/13591?sharedid=web';
	}

	return null;
}
